import React, { useState, useCallback } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../ui/card";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Progress } from "../ui/progress";
import { CheckCircle, Play, Lock, Target } from "lucide-react";
import { getInteractiveComponent } from "./InteractiveElementRegistry";

// Content schema types
interface LessonContentSchema {
  version: string;
  metadata: {
    title: string;
    description: string;
    difficulty: "beginner" | "intermediate" | "advanced";
    estimatedDuration: number;
    category: string;
    tags: string[];
  };
  objectives: string[];
  outcomes: string[];
  prerequisites: string[];
  pages: LessonPageSchema[];
}

interface LessonPageSchema {
  id: string;
  title: string;
  description?: string;
  content: LessonContentBlock[];
  interactiveElements?: InteractiveElementSchema[];
  requiredForCompletion: boolean;
  objectives?: string[];
}

interface LessonContentBlock {
  type: "text" | "heading" | "list" | "image" | "video" | "callout" | "code";
  content: string | string[];
  metadata?: {
    level?: number; // for headings
    ordered?: boolean; // for lists
    src?: string; // for images/videos
    alt?: string; // for images
    language?: string; // for code
    variant?: "info" | "warning" | "tip" | "exercise"; // for callouts
  };
}

interface InteractiveElementSchema {
  id: string;
  type:
    | "color-wheel"
    | "harmony-explorer"
    | "color-mixer"
    | "quiz"
    | "exercise"
    | "interactive-demo";
  title: string;
  description?: string;
  required: boolean;
  props?: Record<string, any>;
  completionCriteria?: {
    type: "interaction" | "score" | "time" | "manual";
    threshold?: number;
  };
}

// Export types for external use
export type {
  LessonContentSchema,
  LessonPageSchema,
  LessonContentBlock,
  InteractiveElementSchema,
};

interface LessonTemplateRendererProps {
  lessonContent: LessonContentSchema;
  onPageComplete?: (pageId: string) => void;
  onInteractiveComplete?: (elementId: string, result: any) => void;
  onLessonComplete?: () => void;
  completedPages?: Set<string>;
  completedElements?: Set<string>;
}

// Component factory for rendering different content types
const ContentBlockRenderer: React.FC<{ block: LessonContentBlock }> = ({
  block,
}) => {
  switch (block.type) {
    case "heading":
      const level = block.metadata?.level || 1;
      const HeadingLevel = Math.min(level, 6) as 1 | 2 | 3 | 4 | 5 | 6;
      const className = `font-semibold mb-4 ${
        level === 1
          ? "text-2xl"
          : level === 2
            ? "text-xl"
            : level === 3
              ? "text-lg"
              : "text-base"
      }`;

      if (HeadingLevel === 1) {
        return <h1 className={className}>{block.content as string}</h1>;
      } else if (HeadingLevel === 2) {
        return <h2 className={className}>{block.content as string}</h2>;
      } else if (HeadingLevel === 3) {
        return <h3 className={className}>{block.content as string}</h3>;
      } else if (HeadingLevel === 4) {
        return <h4 className={className}>{block.content as string}</h4>;
      } else if (HeadingLevel === 5) {
        return <h5 className={className}>{block.content as string}</h5>;
      } else {
        return <h6 className={className}>{block.content as string}</h6>;
      }

    case "text":
      return (
        <p className="text-base leading-relaxed mb-4">
          {block.content as string}
        </p>
      );

    case "list":
      const items = block.content as string[];
      const isOrdered = block.metadata?.ordered || false;
      const ListTag = isOrdered ? "ol" : "ul";
      return (
        <ListTag
          className={`mb-4 pl-6 ${isOrdered ? "list-decimal" : "list-disc"}`}
        >
          {items.map((item, index) => (
            <li key={index} className="mb-1">
              {item}
            </li>
          ))}
        </ListTag>
      );

    case "image":
      return (
        <div className="mb-4">
          <img
            src={block.metadata?.src || ""}
            alt={block.metadata?.alt || ""}
            className="max-w-full h-auto rounded-lg border"
          />
          {block.metadata?.alt && (
            <p className="text-sm text-muted-foreground mt-2 italic">
              {block.metadata.alt}
            </p>
          )}
        </div>
      );

    case "video":
      return (
        <div className="mb-4">
          <video
            src={block.metadata?.src || ""}
            controls
            className="max-w-full h-auto rounded-lg border"
          />
        </div>
      );

    case "callout":
      const variant = block.metadata?.variant || "info";
      const variantStyles = {
        info: "bg-blue-50 border-blue-200 text-blue-800",
        warning: "bg-yellow-50 border-yellow-200 text-yellow-800",
        tip: "bg-green-50 border-green-200 text-green-800",
        exercise: "bg-purple-50 border-purple-200 text-purple-800",
      };
      return (
        <div className={`p-4 rounded-lg border mb-4 ${variantStyles[variant]}`}>
          <div className="font-medium mb-2">
            {variant === "info" && "💡 Information"}
            {variant === "warning" && "⚠️ Warning"}
            {variant === "tip" && "✨ Tip"}
            {variant === "exercise" && "🎯 Exercise"}
          </div>
          <p>{block.content as string}</p>
        </div>
      );

    case "code":
      return (
        <div className="mb-4">
          <pre className="bg-gray-100 p-4 rounded-lg overflow-x-auto">
            <code className="text-sm">{block.content as string}</code>
          </pre>
        </div>
      );

    default:
      return (
        <div className="mb-4 p-4 border border-dashed rounded-lg">
          <p className="text-muted-foreground">
            Unknown content type: {block.type}
          </p>
        </div>
      );
  }
};

// Component factory for interactive elements
const InteractiveElementRenderer: React.FC<{
  element: InteractiveElementSchema;
  onComplete?: (elementId: string, result: any) => void;
  isCompleted?: boolean;
}> = ({ element, onComplete, isCompleted }) => {
  const handleComplete = useCallback(
    (result: any) => {
      onComplete?.(element.id, result);
    },
    [element.id, onComplete],
  );

  // Get the appropriate component from the registry
  const InteractiveComponent = getInteractiveComponent(element.type);

  return (
    <InteractiveComponent
      element={element}
      onComplete={handleComplete}
      isCompleted={isCompleted}
    />
  );
};

export const LessonTemplateRenderer: React.FC<LessonTemplateRendererProps> = ({
  lessonContent,
  onPageComplete,
  onInteractiveComplete: _onInteractiveComplete,
  onLessonComplete,
  completedPages = new Set(),
  completedElements = new Set(),
}) => {
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [_localCompletedElements, _setLocalCompletedElements] =
    useState<Set<string>>(completedElements);

  const currentPage = lessonContent.pages[currentPageIndex];
  const isPageCompleted = completedPages.has(currentPage?.id) || false;

  const handleInteractiveComplete = useCallback(
    (elementId: string, _result: any) => {
      _setLocalCompletedElements(
        (prev: Set<string>) => new Set([...prev, elementId]),
      );

      // Check if all required elements on this page are completed
      const requiredElements =
        currentPage?.interactiveElements?.filter((el) => el.required) || [];
      const allRequiredCompleted = requiredElements.every(
        (el) => completedElements.has(el.id) || elementId === el.id,
      );

      if (allRequiredCompleted && currentPage?.requiredForCompletion) {
        onPageComplete?.(currentPage.id);
      }
    },
    [currentPage, completedElements, onPageComplete],
  );

  const handleNextPage = useCallback(() => {
    if (currentPageIndex < lessonContent.pages.length - 1) {
      setCurrentPageIndex((prev) => prev + 1);
    }
  }, [currentPageIndex, lessonContent.pages.length]);

  const handlePrevPage = useCallback(() => {
    if (currentPageIndex > 0) {
      setCurrentPageIndex((prev) => prev - 1);
    }
  }, [currentPageIndex]);

  if (!currentPage) {
    return (
      <div className="flex items-center justify-center h-64">
        <p>Lesson content not available</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <CardTitle className="text-xl flex items-center gap-2">
                {currentPage.title}
                {isPageCompleted && (
                  <CheckCircle className="h-5 w-5 text-green-500" />
                )}
                {currentPage.requiredForCompletion && !isPageCompleted && (
                  <Lock className="h-5 w-5 text-orange-500" />
                )}
              </CardTitle>
              {currentPage.description && (
                <CardDescription className="mt-2">
                  {currentPage.description}
                </CardDescription>
              )}
              {currentPage.objectives && (
                <div className="mt-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Target className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium">
                      Learning Objectives:
                    </span>
                  </div>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    {currentPage.objectives.map((objective, index) => (
                      <li key={index}>• {objective}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline">
                Page {currentPageIndex + 1} of {lessonContent.pages.length}
              </Badge>
              {currentPage.requiredForCompletion && (
                <Badge variant="secondary">Required</Badge>
              )}
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Page Content */}
      <Card>
        <CardContent className="pt-6">
          <div className="prose max-w-none space-y-4">
            {currentPage.content.map((block, index) => (
              <ContentBlockRenderer key={index} block={block} />
            ))}
          </div>

          {/* Interactive Elements */}
          {currentPage.interactiveElements &&
            currentPage.interactiveElements.length > 0 && (
              <div className="mt-8 space-y-6">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Play className="h-5 w-5" />
                  Interactive Elements
                </h3>
                {currentPage.interactiveElements.map((element) => (
                  <InteractiveElementRenderer
                    key={element.id}
                    element={element}
                    onComplete={handleInteractiveComplete}
                    isCompleted={completedElements.has(element.id)}
                  />
                ))}
              </div>
            )}
        </CardContent>
      </Card>

      {/* Enhanced Page Navigation */}
      <Card>
        <CardContent className="pt-6">
          <div className="space-y-4">
            {/* Progress Overview */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <span className="text-sm font-medium">Lesson Progress</span>
                <div className="flex items-center gap-2">
                  <div className="text-sm text-muted-foreground">
                    Page {currentPageIndex + 1} of {lessonContent.pages.length}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    (
                    {Math.round(
                      ((currentPageIndex + 1) / lessonContent.pages.length) *
                        100,
                    )}
                    %)
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPageIndex(0)}
                  disabled={currentPageIndex === 0}
                >
                  First
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handlePrevPage}
                  disabled={currentPageIndex === 0}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleNextPage}
                  disabled={
                    currentPageIndex === lessonContent.pages.length - 1 ||
                    (currentPage.requiredForCompletion && !isPageCompleted)
                  }
                >
                  Next
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setCurrentPageIndex(lessonContent.pages.length - 1)
                  }
                  disabled={currentPageIndex === lessonContent.pages.length - 1}
                >
                  Last
                </Button>
              </div>
            </div>

            {/* Visual Progress Bar */}
            <div className="relative">
              <Progress
                value={
                  ((currentPageIndex + 1) / lessonContent.pages.length) * 100
                }
                className="w-full h-2"
              />
              <div className="flex justify-between mt-2">
                {lessonContent.pages.map((page, index) => (
                  <button
                    key={page.id}
                    onClick={() => setCurrentPageIndex(index)}
                    className={`w-3 h-3 rounded-full border-2 transition-colors ${
                      index === currentPageIndex
                        ? "bg-primary border-primary"
                        : index < currentPageIndex
                          ? "bg-green-500 border-green-500"
                          : "bg-background border-muted-foreground hover:border-primary"
                    }`}
                    title={page.title}
                    disabled={
                      index > currentPageIndex &&
                      lessonContent.pages[index - 1]?.requiredForCompletion &&
                      !completedPages.has(lessonContent.pages[index - 1].id)
                    }
                  />
                ))}
              </div>
            </div>

            {/* Page Jump Dropdown */}
            <div className="flex items-center gap-4">
              <label className="text-sm font-medium">Jump to page:</label>
              <select
                value={currentPageIndex}
                onChange={(e) => setCurrentPageIndex(parseInt(e.target.value))}
                className="px-3 py-1 border rounded-md text-sm"
              >
                {lessonContent.pages.map((page, index) => (
                  <option
                    key={page.id}
                    value={index}
                    disabled={
                      index > currentPageIndex &&
                      page.requiredForCompletion &&
                      !completedPages.has(lessonContent.pages[index - 1]?.id)
                    }
                  >
                    {index + 1}. {page.title}
                    {completedPages.has(page.id) && " ✓"}
                    {page.requiredForCompletion &&
                      !completedPages.has(page.id) &&
                      " (required)"}
                  </option>
                ))}
              </select>
            </div>

            {/* Navigation Status */}
            {currentPage.requiredForCompletion && !isPageCompleted && (
              <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
                <p className="text-sm text-orange-800">
                  <Lock className="inline h-4 w-4 mr-1" />
                  Complete this page to unlock the next one
                </p>
              </div>
            )}

            {/* Completion Status */}
            {isPageCompleted && (
              <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm text-green-800">
                  <CheckCircle className="inline h-4 w-4 mr-1" />
                  This page is complete! You can continue to the next page.
                </p>
              </div>
            )}

            {/* Final Navigation Actions */}
            <div className="flex items-center justify-between pt-4 border-t">
              <div className="text-sm text-muted-foreground">
                {completedPages.size} of {lessonContent.pages.length} pages
                completed
              </div>
              {currentPageIndex === lessonContent.pages.length - 1 && (
                <Button
                  onClick={() => {
                    // Handle lesson completion
                    if (completedPages.size === lessonContent.pages.length) {
                      onLessonComplete?.();
                    }
                  }}
                  disabled={completedPages.size !== lessonContent.pages.length}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Finish Lesson
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
